/**
 * /admin/noise-anchors — multi-anchor noise filter management.
 *
 * Two add channels:
 *   1. ➕ 手动添加：粘贴一条噪声原文 + 备注
 *   2. 🤖 用 14B 推荐：sample → uncovered filter → 14B picks → admin approves
 *
 * Anchors list sorted by hitCount desc so most-effective ones rise to top.
 * Archived anchors hidden by default; toggle to see.
 */

import { useState, type FormEvent } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  type ListNoiseAnchorsResponse,
  type NoiseAnchorItem,
  Routes,
  type SuggestNoiseAnchorsResponse,
} from '@tg-leads/contracts';

import { api } from '../../lib/api';
import { toastError, toastInfo, toastSuccess } from '../../lib/toast';

export function NoiseAnchorsPage() {
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [suggestion, setSuggestion] =
    useState<SuggestNoiseAnchorsResponse | null>(null);

  const list = useQuery({
    queryKey: ['noiseAnchors', showArchived],
    queryFn: () =>
      api.get<ListNoiseAnchorsResponse>(
        `${Routes.admin.noiseAnchors}?includeArchived=${showArchived}&limit=500`,
      ),
    refetchInterval: 30_000,
  });

  const updateMut = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.patch<{ item: NoiseAnchorItem }>(
        `${Routes.admin.noiseAnchors}/${id}`,
        { archived },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['noiseAnchors'] }),
    onError: (e) => toastError(e),
  });

  const suggestMut = useMutation({
    mutationFn: () =>
      api.post<SuggestNoiseAnchorsResponse>(
        Routes.admin.noiseAnchorsSuggest,
        { sampleSize: 200, lookbackHours: 168 },
      ),
    onSuccess: (data) => {
      setSuggestion(data);
      if (data.candidates.length === 0) {
        toastInfo(
          `14B 分析了 ${data.sampledCount} 条样本，其中 ${data.uncoveredCount} 条没被现有 anchor 拦住，` +
            `但 14B 没挑出明确的噪声候选（可能你的过滤已经很好）`,
        );
      }
    },
    onError: (e) => toastError(e),
  });

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">噪声样本管理（L1 多 anchor）</h1>
          <p className="text-sm text-slate-500 mt-1">
            CascadeJudgeService L1 用这些 anchor 算 max-cosine，超过阈值的入站消息直接丢弃。
            按 hitCount 排序，命中越多的放越上面。
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className="btn-secondary"
            onClick={() => setShowAddDialog(true)}
          >
            ➕ 手动添加
          </button>
          <button
            className="btn-primary"
            onClick={() => suggestMut.mutate()}
            disabled={suggestMut.isPending}
          >
            {suggestMut.isPending
              ? '🤖 14B 分析中 (30-90s)…'
              : '🤖 用 14B 推荐'}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-slate-500">
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          显示已归档
        </label>
        <div>共 {items.length} 条 anchor</div>
      </div>

      {showAddDialog && (
        <AddAnchorDialog
          onClose={() => setShowAddDialog(false)}
          onSuccess={() => {
            setShowAddDialog(false);
            qc.invalidateQueries({ queryKey: ['noiseAnchors'] });
          }}
        />
      )}

      {suggestion && (
        <SuggestionReviewPanel
          response={suggestion}
          onClose={() => setSuggestion(null)}
          onAccept={() => {
            setSuggestion(null);
            qc.invalidateQueries({ queryKey: ['noiseAnchors'] });
          }}
        />
      )}

      <div className="card overflow-x-auto">
        {list.isLoading ? (
          <div className="text-sm text-slate-500">加载中…</div>
        ) : !items.length ? (
          <div className="text-sm text-slate-500">
            还没有 anchor。点 ➕ 手动添加，或 🤖 让 14B 从近期消息里找。
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase border-b">
                <th className="py-2 pr-4">原文</th>
                <th className="py-2 pr-4">备注</th>
                <th className="py-2 pr-4">来源</th>
                <th className="py-2 pr-4">命中</th>
                <th className="py-2 pr-4">最近命中</th>
                <th className="py-2 pr-4">添加于</th>
                <th className="py-2 pr-4">状态</th>
                <th className="py-2 pr-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr
                  key={a.id}
                  className={`border-b last:border-0 ${a.archived ? 'opacity-50' : ''}`}
                >
                  <td className="py-3 pr-4 max-w-[28rem] truncate" title={a.text}>
                    {a.text}
                  </td>
                  <td className="py-3 pr-4 text-xs text-slate-500">
                    {a.note ?? '—'}
                  </td>
                  <td className="py-3 pr-4">
                    <SourceBadge source={a.source} />
                  </td>
                  <td
                    className={`py-3 pr-4 font-mono ${
                      a.hitCount === 0 ? 'text-slate-400' : 'text-green-700'
                    }`}
                  >
                    {a.hitCount}
                  </td>
                  <td className="py-3 pr-4 text-xs text-slate-500">
                    {a.lastHitAt
                      ? new Date(a.lastHitAt).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="py-3 pr-4 text-xs text-slate-500">
                    {new Date(a.addedAt).toLocaleDateString()}
                  </td>
                  <td className="py-3 pr-4">
                    {!a.embeddingReady ? (
                      <span className="badge-warn">embedding 待补</span>
                    ) : a.archived ? (
                      <span className="badge-neutral">已归档</span>
                    ) : (
                      <span className="badge-ok">激活</span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <button
                      className="btn-secondary text-xs"
                      onClick={() =>
                        updateMut.mutate({
                          id: a.id,
                          archived: !a.archived,
                        })
                      }
                      disabled={updateMut.isPending}
                    >
                      {a.archived ? '取消归档' : '归档'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SourceBadge({ source }: { source: NoiseAnchorItem['source'] }) {
  const label = source === 'manual' ? '手动' : 'AI';
  const cls = source === 'manual' ? 'badge-ok' : 'badge-warn';
  return <span className={cls}>{label}</span>;
}

function AddAnchorDialog({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [text, setText] = useState('');
  const [note, setNote] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      api.post(Routes.admin.noiseAnchors, {
        text: text.trim(),
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      toastSuccess('已添加噪声 anchor');
      onSuccess();
    },
    onError: (e) => toastError(e),
  });

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <form
        className="card w-[28rem] space-y-3"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          mut.mutate();
        }}
      >
        <h2 className="text-lg font-semibold">手动添加噪声 anchor</h2>
        <div>
          <label className="label">原文（粘贴一条噪声消息）</label>
          <textarea
            className="input font-mono text-xs"
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder='例: "@all 群规请遵守，禁止刷屏" / "🎲 骰子游戏：1"'
            required
          />
        </div>
        <div>
          <label className="label">备注（可选，分类用）</label>
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="如：xxx bot 推送 / 节日模板"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            取消
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!text.trim() || mut.isPending}
          >
            {mut.isPending ? '保存中…' : '保存（同步算 embedding）'}
          </button>
        </div>
      </form>
    </div>
  );
}

function SuggestionReviewPanel({
  response,
  onClose,
  onAccept,
}: {
  response: SuggestNoiseAnchorsResponse;
  onClose: () => void;
  onAccept: () => void;
}) {
  // Pre-select all candidates by default (admin un-checks bad ones)
  const [selected, setSelected] = useState<Set<number>>(
    new Set(response.candidates.map((_, i) => i)),
  );
  const [notes, setNotes] = useState<Record<number, string>>({});

  const accept = useMutation({
    mutationFn: () => {
      const items = Array.from(selected).map((i) => {
        const c = response.candidates[i]!;
        return { text: c.text, note: notes[i] || c.reason };
      });
      return api.post(Routes.admin.noiseAnchorsBulk, {
        items,
        source: 'ai_suggested',
      });
    },
    onSuccess: () => {
      toastSuccess(`已添加 ${selected.size} 条 AI 推荐 anchor`);
      onAccept();
    },
    onError: (e) => toastError(e),
  });

  return (
    <div className="card border-2 border-indigo-300 space-y-3">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-base font-semibold text-indigo-900">
            🤖 14B 推荐结果
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            采样 {response.sampledCount} 条消息 · {response.uncoveredCount} 条未被现有 anchor 覆盖 ·
            14B 挑出 {response.candidates.length} 条候选噪声
          </p>
        </div>
        <button
          className="text-slate-400 hover:text-slate-700 text-xl leading-none"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      {response.candidates.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center">
          没有可推荐的候选。可能样本太少，或者 14B 觉得这些消息都可能是线索（保守判断）。
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-500">
            勾选要采纳的候选，可以改备注。点"添加选中的 N 条"批量入库。
          </div>
          <ul className="divide-y border rounded max-h-[28rem] overflow-y-auto">
            {response.candidates.map((c, i) => (
              <li key={i} className="p-3 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selected.has(i)}
                    onChange={(e) =>
                      setSelected((s) => {
                        const n = new Set(s);
                        if (e.target.checked) n.add(i);
                        else n.delete(i);
                        return n;
                      })
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono break-words bg-slate-50 px-2 py-1 rounded">
                      {c.text}
                    </div>
                    <div className="text-xs text-indigo-700 mt-1">
                      14B: {c.reason}
                    </div>
                    {(c.maxCosineToExisting > 0.5 || c.maxCosineWithinBatch > 0.5) && (
                      <div className="text-[10px] text-orange-600 mt-0.5 flex flex-wrap gap-x-3">
                        {c.maxCosineToExisting > 0.5 && (
                          <span>
                            vs 现有 anchor: {c.maxCosineToExisting.toFixed(3)}
                          </span>
                        )}
                        {c.maxCosineWithinBatch > 0.5 && (
                          <span>
                            vs 本批已选: {c.maxCosineWithinBatch.toFixed(3)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
                {selected.has(i) && (
                  <input
                    className="input text-xs"
                    placeholder={`备注（默认: ${c.reason.slice(0, 30)}…）`}
                    value={notes[i] ?? ''}
                    onChange={(e) =>
                      setNotes((n) => ({ ...n, [i]: e.target.value }))
                    }
                  />
                )}
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={onClose}>
              取消
            </button>
            <button
              className="btn-primary"
              disabled={selected.size === 0 || accept.isPending}
              onClick={() => accept.mutate()}
            >
              {accept.isPending
                ? '保存中…'
                : `添加选中的 ${selected.size} 条`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
